import PersonOffIcon from '@mui/icons-material/PersonOff';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import { latamLocale } from 'src/shared/utils/currency';
import {
  AccountBox,
  Article,
  DashboardCustomize,
  DesignServices,
  Error,
  Extension,
  Folder,
  Group,
  HelpCenter,
  Image,
  Logout,
  MonetizationOnOutlined,
  PersonAdd,
  SmartDisplay,
  Store,
  Sync,
  TabletMac,
  Wallet,
  Lock,
  Payments,
  Help,
  Mail,
  Report,
  Source,
  Dataset,
  Diversity3
} from '@mui/icons-material';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { type NavBarItem } from '../Navbar/NavbarContent';
import { CountryCodes } from 'src/shared/utils/currency';
import { AccountCircle } from '@mui/icons-material';
import { CreditCard } from '@mui/icons-material';
import { BarChart } from '@mui/icons-material';
import { ManageAccounts } from '@mui/icons-material';
import { getFeatureRoute } from './utils';

const ayudaUrlsByCountry: Record<string, string[]> = {
  [CountryCodes.SPAIN]: [
    'https://www.payflow.es/materiales',
    'https://24272312.hs-sites.com/',
    'https://www.youtube.com/playlist?list=PL5g6S5TzFLY8_ezRsfXL95TdiaG67cjMG',
    'mailto:soporte_empresas@payflow.es'
  ],
  [CountryCodes.PORTUGAL]: [
    'https://www.payflow.es/pt-materiales-payflow',
    'https://24272312.hs-sites.com/',
    'https://www.youtube.com/playlist?list=PL5g6S5TzFLY8771XiAcdwtNlG-PQ1bsC-',
    'mailto:suporte_empresas@payflow.es'
  ],
  [CountryCodes.COLOMBIA]: [
    'https://www.payflow.es/co-materiales-payflow',
    'https://24272312.hs-sites.com/',
    'https://www.youtube.com/playlist?list=PL5g6S5TzFLY-jjrmIdlKdxP7vIeHVEhhi',
    'mailto:soporte_empresas@payflowapp.co'
  ],
  [CountryCodes.PERU]: [
    'https://www.payflow.es/pe-materiales-payflow',
    'https://24272312.hs-sites.com/',
    'https://www.youtube.com/playlist?list=PL5g6S5TzFLY8u30ZRtwSRUTMK43GISWRT',
    'mailto:soporte_empresas@payflowapp.pe'
  ]
};

const navConfig = (
  t: any,
  superAdmin: boolean,
  company: any,
  holding: any,
  dataChangeRequestsCount: number,
  hasIntegration: boolean
): NavBarItem[] => {
  const companyLocale = company?.locale;
  const flexFlowEnabled = !!company?.flexFlow;
  const payflowEnabled = !!company?.payflow;
  const isSpain = companyLocale === CountryCodes.SPAIN;

  const ayudaBaseLinks = [
    {
      title: t('items.communication materials'),
      icon: Image,
      isExternal: true
    },
    {
      title: t('items.faq'),
      icon: Help,
      isExternal: true
    },
    {
      title: t('items.tutorials'),
      icon: SmartDisplay,
      isExternal: true
    },
    {
      title: t('items.support'),
      icon: Mail,
      isExternal: true
    }
  ];

  const getAyudaLinks = (companyLocale: string) => {
    const urls = ayudaUrlsByCountry[companyLocale];
    if (!urls) return [];
    return ayudaBaseLinks.map((link, idx) => ({
      ...link,
      href: urls[idx] || ''
    }));
  };

  const getFlexflowRoute = (route: string): string => {
    return getFeatureRoute({ isFeatureEnabled: flexFlowEnabled, route });
  };

  const getPayflowRoute = (route: string): string => {
    return getFeatureRoute({ isFeatureEnabled: payflowEnabled, route });
  };

  return [
    holding?.settings?.features?.selfFunding && {
      title: t('items.saldo'),
      href: '/saldo',
      icon: AccountBalanceWalletIcon,
      heroOnly: true
    },

    // 1. Reporte (first-level link)
    {
      title: t('items.overview'),
      href: '/overview',
      icon: BarChart
    },
    // 2. Gestión de Empleados
    {
      icon: Group,
      title: t('subheaders.employees'),
      items: [
        !hasIntegration && {
          title: t('items.add'),
          href: '/add-employee-choice',
          icon: PersonAdd
        },
        !hasIntegration && {
          title: t('items.inactivate employee'),
          href: '/employee/inactivate',
          icon: PersonRemoveIcon
        },
        {
          title: t('items.restrict employees'),
          href: '/employee/restricted',
          icon: PersonOffIcon
        },
        {
          title: !hasIntegration || superAdmin ? t('items.view/edit employees') : t('items.view employees'),
          href: '/list-employees',
          icon: Group
        },
        company?.settings?.extraPayment && {
          title: t('items.collectives'),
          href: '/collectives',
          icon: Diversity3,
          heroOnly: true
        },
        latamLocale.includes(company?.locale) &&
          holding?.settings?.features?.dataRevision === true && {
            title: t('items.review data'),
            href: '/data-review',
            icon: PlaylistAddCheckIcon,
            badge: dataChangeRequestsCount
          }
      ].filter(Boolean)
    },
    // 3. Salario Bajo Demanda
    {
      icon: TabletMac,
      title: t('subheaders.demand-salary'),
      items: [
        {
          title: t('items.transactions'),
          href: getPayflowRoute('/history'),
          icon: Payments
        },
        {
          title: t('items.configuration'),
          href: getPayflowRoute('/settings/configuration'),
          icon: DesignServices
        }
      ]
    },
    // 4. Retribución Flexible (Spain + flexFlow)
    ...(isSpain
      ? [
          {
            icon: CreditCard,
            title: t('subheaders.flexflow'),
            items: [
              {
                title: t('items.manage employees'),
                href: getFlexflowRoute('/flexflow/employees'),
                icon: ManageAccounts
              },
              {
                title: t('items.transactions history'),
                href: getFlexflowRoute('/flexflow/history'),
                icon: Payments
              },
              {
                title: t('items.applications'),
                href: getFlexflowRoute('/holding/flexflow/application'),
                icon: Article
              },
              {
                title: t('items.configuration'),
                href: getFlexflowRoute('/flexflow/configuration'),
                icon: DesignServices
              }
            ]
          }
        ]
      : []),
    // 5. Nómina
    {
      icon: Wallet,
      title: t('subheaders.payroll'),
      items: [
        {
          title: t('items.payroll'),
          href: '/payroll',
          icon: Source
        },
        {
          title: t('items.liquidation list'),
          href: '/liquidation-list',
          icon: Report // TODO
        }
      ]
    },
    // 6. Integraciones
    {
      icon: Extension,
      title: t('subheaders.integrations'),
      heroOnly: true,
      items: [
        {
          title: t('items.integrations'),
          href: '/settings/integrations',
          icon: Dataset
        },
        {
          title: t('items.SFTP'),
          href: '/settings/sftp',
          icon: Folder
        }
      ]
    },
    // 7. Ayuda (external links, locale-based)
    {
      icon: HelpCenter,
      title: t('subheaders.help'),
      items: getAyudaLinks(companyLocale)
    },
    // 8. Cuenta
    {
      icon: AccountCircle,
      title: t('subheaders.account'),
      items: [
        {
          title: t('items.companies'),
          href: '/settings/companies',
          icon: Store
        },
        !holding?.ssoSettings && {
          title: t('items.admin list'),
          href: '/list-admins',
          icon: AccountBox // TODO
        },
        holding?.settings?.holdedLink && {
          title: t('items.billing'),
          href: holding?.settings?.holdedLink || '',
          icon: ReceiptLongIcon,
          isExternal: !!holding?.settings?.holdedLink,
          heroOnly: true
        },
        {
          title: t('items.personalization'),
          href: '/settings/personalization',
          icon: DashboardCustomize,
          heroOnly: true
        },
        {
          title: t('items.password'),
          href: '/settings/password',
          icon: Lock // NOT FOUND
        },
        {
          title: t('items.logout'),
          action: 'logout',
          icon: Logout
        }
      ].filter(Boolean)
    }
  ].filter(Boolean);
};

export default navConfig;
